import React, { useState } from 'react';
import { Mail, Send, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import CustomDropdown from '../ui/CustomDropdown';

const API_BASE = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be';

/**
 * Mail Test Page - Pour tester l'envoi de mails depuis le serveur
 */
const MailTestPage = () => {
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('Test mail Cabaza');
    const [message, setMessage] = useState('Ceci est un mail de test envoyé depuis Cabaza.');
    const [mailType, setMailType] = useState('simple');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);

    const mailTypeOptions = [
        { value: 'simple', label: 'Simple (texte)' },
        { value: 'html', label: 'HTML' },
        { value: 'template', label: 'Template Cabaza' }
    ];

    const sendTestMail = async () => {
        if (!email) {
            alert('Veuillez entrer une adresse email');
            return;
        }

        setLoading(true);
        const timestamp = new Date().toLocaleTimeString();

        try {
            const response = await fetch(`${API_BASE}/rest/cabaza/mail/test`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    to: email,
                    subject: subject,
                    message: message,
                    type: mailType
                })
            });

            const data = await response.json();
            const success = data.success ?? data.SUCCESS;
            const msg = data.message ?? data.MESSAGE ?? (success ? 'Mail envoyé' : 'Erreur');

            setResults(prev => [{
                timestamp,
                email,
                type: mailType,
                success,
                message: msg
            }, ...prev]);

        } catch (error) {
            setResults(prev => [{
                timestamp,
                email,
                type: mailType,
                success: false,
                message: error.message
            }, ...prev]);
        } finally {
            setLoading(false);
        }
    };

    const sendMultipleMails = async (count) => {
        for (let i = 0; i < count; i++) {
            await sendTestMail();
            // Petit délai entre chaque envoi
            await new Promise(resolve => setTimeout(resolve, 500));
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 p-6">
            <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                    <div className="flex items-center gap-3 mb-6">
                        <Mail className="w-8 h-8 text-blue-500" />
                        <h1 className="text-2xl font-bold text-gray-800">Test d'envoi de mails</h1>
                    </div>

                    <div className="space-y-4">
                        {/* Email destinataire */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email destinataire
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="test@example.com"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Sujet */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Sujet
                            </label>
                            <input
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Message */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Message
                            </label>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={3}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Type de mail */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Type de mail
                            </label>
                            <CustomDropdown
                                value={mailType}
                                onChange={(val) => setMailType(val)}
                                options={mailTypeOptions}
                                className="w-full"
                            />
                        </div>

                        {/* Boutons d'envoi */}
                        <div className="flex flex-wrap gap-3 pt-4">
                            <Button
                                onClick={sendTestMail}
                                disabled={loading}
                                className="flex items-center gap-2"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4" />
                                )}
                                Envoyer 1 mail
                            </Button>

                            <Button
                                onClick={() => sendMultipleMails(3)}
                                disabled={loading}
                                variant="secondary"
                            >
                                Envoyer 3 mails
                            </Button>

                            <Button
                                onClick={() => sendMultipleMails(5)}
                                disabled={loading}
                                variant="secondary"
                            >
                                Envoyer 5 mails
                            </Button>

                            <Button
                                onClick={() => sendMultipleMails(10)}
                                disabled={loading}
                                variant="secondary"
                            >
                                Envoyer 10 mails
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Résultats */}
                {results.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-800">
                                Résultats ({results.length})
                            </h2>
                            <button
                                onClick={() => setResults([])}
                                className="text-sm text-gray-500 hover:text-gray-700"
                            >
                                Effacer
                            </button>
                        </div>

                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {results.map((result, index) => (
                                <div
                                    key={index}
                                    className={`flex items-start gap-3 p-3 rounded-lg ${
                                        result.success ? 'bg-green-50' : 'bg-red-50'
                                    }`}
                                >
                                    {result.success ? (
                                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                    ) : (
                                        <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="font-medium">{result.timestamp}</span>
                                            <span className="text-gray-500">→</span>
                                            <span className="truncate">{result.email}</span>
                                            <span className="px-2 py-0.5 bg-gray-200 rounded text-xs">
                                                {result.type}
                                            </span>
                                        </div>
                                        <p className={`text-sm mt-1 ${
                                            result.success ? 'text-green-700' : 'text-red-700'
                                        }`}>
                                            {result.message}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MailTestPage;
